import Layout from 'antd/lib/layout';
import React from 'react';
import { Helmet } from 'react-helmet';
import { LinkedInPopUp } from 'react-linkedin-login-oauth2';
import { Route, Switch, useLocation } from 'react-router-dom';

import Error from 'pages/apps/survey/Error';
import LinkedInPostAuth from 'pages/apps/survey/LinkedInPostAuth';
import Payment from 'pages/apps/survey/PaymentPending';
import PaymentError from 'pages/apps/survey/PaymentError';
import PaymentClaim from 'pages/apps/survey/PaymentClaim';
import Flags from 'pages/apps/survey/Flags';
import SurveyClosed from 'pages/apps/survey/SurveyClosed';
import Availability from 'pages/apps/survey/Availability';
import ContactInformation from 'pages/apps/survey/ContactInformation';
import VerificationAcknowledgement from 'pages/apps/survey/VerificationAcknowledgement';
import { BLOCK_PAGES, NON_BLOCK_PAGES, ROUTES } from 'shared/routes/constants';
import Content from 'uicontainers/_shared/layout/content/Content';
import ErrorUI from 'uicontainers/apps/survey/Error';
import ReferralsClosed from 'uicontainers/apps/survey/ReferralsClosed';
import TopLevelLoadingSpinner from 'uicontainers/apps/survey/_shared/TopLevelLoadingSpinner';
import LayoutRefresh from 'uicontainers/_shared/layout_refresh/index';

import {
  BLOCK_TYPE_TO_COMPONENT_MAP,
  BLOCK_TYPE_TO_COMPONENT_MAP_REFRESH,
  BLOCK_TYPE_TO_COMPONENT_MAP_CONSULTATION,
  BLOCK_TYPE_TO_COMPONENT_MAP_CONSULTATION_AI,
} from './constants';
import { useAuthorize, useSurveyRouteConfigSelectors } from './hooks';
import Terms from '../../_shared/Terms';
import PrivacyPolicy from '../../_shared/PrivacyPolicy';
import { useSurveyContent } from '../../../../shared/hooks';

export const OpenRoutes = () => {
  const { surveyContent } = useSurveyContent();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const template = queryParams.get('survey_template');
  const RefreshEnabled =
    surveyContent?.survey_template === 'SURVEY_2025' ||
    template === 'SURVEY_2025' ||
    surveyContent?.survey_template === 'CONSULTATIONS_2025' ||
    template === 'CONSULTATIONS_2025';
  return (
    <>
      {!RefreshEnabled && (
        <Layout>
          <Content>
            <Switch>
              <Route path={ROUTES.SURVEY.PATHS.PAYMENT_PENDING} component={Payment} />
              <Route path={ROUTES.SURVEY.PATHS.PAYMENT_ERROR} component={PaymentError} />
              <Route path={ROUTES.SURVEY.PATHS.PAYMENT_CLAIM_BY_ID} component={PaymentClaim} />
              <Route
                path={ROUTES.SURVEY.PATHS.VERIFICATION_ACKNOWLEDGEMENT}
                component={VerificationAcknowledgement}
              />
              <Route path={ROUTES.SURVEY.PATHS.LINKEDIN_POSTAUTH} component={LinkedInPostAuth} />
              <Route path={ROUTES.SURVEY.PATHS.SURVEY_CLOSED} component={SurveyClosed} />
              <Route path={ROUTES.SURVEY.PATHS.ERROR} component={Error} />
              <Route path={ROUTES.SURVEY.PATHS.TERMS} component={Terms} />
              <Route path={ROUTES.SURVEY.PATHS.PRIVACY_POLICY} component={PrivacyPolicy} />
              <Route path={ROUTES.SURVEY.PATHS.FLAGS} component={Flags} />
              <Route exact path={ROUTES.SURVEY.PATHS.LINKEDIN_POPUP} component={LinkedInPopUp} />
              <Route path={ROUTES.SURVEY.PATHS.REFERRALS_CLOSED} component={ReferralsClosed} />
            </Switch>
          </Content>
        </Layout>
      )}
      {RefreshEnabled && (
        <LayoutRefresh>
          <Switch>
            <Route path={ROUTES.SURVEY.PATHS.PAYMENT_PENDING} component={Payment} />
            <Route path={ROUTES.SURVEY.PATHS.PAYMENT_ERROR} component={PaymentError} />
            <Route path={ROUTES.SURVEY.PATHS.PAYMENT_CLAIM_BY_ID} component={PaymentClaim} />
            <Route
              path={ROUTES.SURVEY.PATHS.VERIFICATION_ACKNOWLEDGEMENT}
              component={VerificationAcknowledgement}
            />
            <Route path={ROUTES.SURVEY.PATHS.LINKEDIN_POSTAUTH} component={LinkedInPostAuth} />
            <Route path={ROUTES.SURVEY.PATHS.SURVEY_CLOSED} component={SurveyClosed} />
            <Route path={ROUTES.SURVEY.PATHS.ERROR} component={Error} />
            <Route path={ROUTES.SURVEY.PATHS.TERMS} component={Terms} />
            <Route path={ROUTES.SURVEY.PATHS.PRIVACY_POLICY} component={PrivacyPolicy} />
            <Route path={ROUTES.SURVEY.PATHS.FLAGS} component={Flags} />
            <Route exact path={ROUTES.SURVEY.PATHS.LINKEDIN_POPUP} component={LinkedInPopUp} />
            <Route path={ROUTES.SURVEY.PATHS.REFERRALS_CLOSED} component={ReferralsClosed} />
          </Switch>
        </LayoutRefresh>
      )}
    </>
  );
};

export const AuthorizedRoutes = () => {
  const {
    isAuthorized,
    isAuthorizing,
    redirectTo,
    blockType,
    content,
  } = useSurveyRouteConfigSelectors();
  const { surveyContent } = useSurveyContent();
  const RefreshEnabled =
    surveyContent?.survey_template === 'SURVEY_2025' ||
    surveyContent?.survey_template === 'CONSULTATIONS_2025';
  useAuthorize();
  const isConsultation = surveyContent?.survey_template === 'CONSULTATIONS_2025';
  const isAi = surveyContent?.consultation_method === 'ai_moderated';
  const blockMap = isAi
    ? BLOCK_TYPE_TO_COMPONENT_MAP_CONSULTATION_AI
    : BLOCK_TYPE_TO_COMPONENT_MAP_CONSULTATION;
  let consultationBlock = 'AVAILABILITY' as 'AVAILABILITY' | 'CONTACT';
  let consultationComponent = blockMap[blockType ?? 'ENTRY'];
  if (isConsultation && blockType === 'CUSTOM') {
    const url = window.location.href;
    const match = url.match(/\/surveys\/([^/]+)/);
    const letter = match ? match[1] : null;
    if (letter === 'i') {
      consultationBlock = 'CONTACT';
    }
    consultationComponent = blockMap[consultationBlock];
  }
  const Component = blockType && BLOCK_TYPE_TO_COMPONENT_MAP[blockType];
  const ComponentRefresh =
    blockType &&
    (isConsultation ? consultationComponent : BLOCK_TYPE_TO_COMPONENT_MAP_REFRESH[blockType]);
  let shouldRenderContentRefresh = Boolean(
    !isAuthorizing && isAuthorized && !redirectTo && ComponentRefresh,
  );
  if (blockType === 'CUSTOM') {
    shouldRenderContentRefresh = true;
  }
  const shouldRenderErrorPageRefresh = !isAuthorizing && !isAuthorized && !redirectTo;

  const shouldRenderContent = Boolean(!isAuthorizing && isAuthorized && !redirectTo && Component);
  const shouldRenderErrorPage = !isAuthorizing && !isAuthorized && !redirectTo;
  const heading = content?.heading ?? 'Survey';
  const subHeading =
    content?.subheading ?? 'Click on the link to continue through the survey process';

  const shouldShowHeader = Boolean(
    !isAuthorizing &&
      isAuthorized &&
      !redirectTo &&
      surveyContent &&
      RefreshEnabled &&
      ComponentRefresh,
  );

  return (
    <>
      {!RefreshEnabled && (
        <Layout>
          <Helmet>
            <title>{heading}</title>
            <meta name="description" content={subHeading} />
          </Helmet>
          <Content>
            {/*
         // @ts-ignore */}
            {shouldRenderContent && Component && <Component />}

            {shouldRenderErrorPage && <ErrorUI />}
            {shouldRenderErrorPage && <Flags />}
          </Content>
        </Layout>
      )}
      {RefreshEnabled && (
        <LayoutRefresh showHeader={shouldShowHeader}>
          <Helmet>
            <title>{heading}</title>
            <meta name="description" content={subHeading} />
          </Helmet>
          {/*
         // @ts-ignore */}
          {shouldRenderContentRefresh && ComponentRefresh && <ComponentRefresh />}
          {shouldRenderErrorPageRefresh && <ErrorUI />}
          {shouldRenderErrorPageRefresh && <Flags />}
        </LayoutRefresh>
      )}
    </>
  );
};

const SurveyRouteConfig = () => {
  const { shouldShowTopLevelLoadingSpinner } = useSurveyRouteConfigSelectors();
  return (
    <div>
      {shouldShowTopLevelLoadingSpinner ? <TopLevelLoadingSpinner /> : null}
      <Switch>
        <Route path={NON_BLOCK_PAGES} component={OpenRoutes} />
        <Route path={BLOCK_PAGES} component={AuthorizedRoutes} />
      </Switch>
    </div>
  );
};

export default SurveyRouteConfig;
